import { Button } from "../components/Button";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
const ButtonsSwitchContext = React.createContext(null);
const useButtonsSwitch = () => {
  const ctx = React.useContext(ButtonsSwitchContext);
  if (!ctx) {
    throw new Error("ButtonsSwitch must be used within a ButtonsSwitchList component");
  }
  return ctx;
};

// Translucent track: a light gray tint on light, a slightly stronger tint on
// dark so the pill still reads against the darker page. Per size, the track
// takes Button's height and radius (24/32/40px, 9/12/15px) so a switch lines
// up with a Button of the same size next to it. The options are plain Buttons
// of that size with no inner padding, and the track border (same tone as the
// outline Button) is an overlay drawn over them rather than a box border
// around them, so at the edges it coincides with the selected pill's own
// border instead of doubling it.
const listStyles = cva(cn("relative inline-flex items-center gap-1", "bg-foreground/[0.04] dark:bg-foreground/[0.06]", "after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit]", "after:border after:border-border-dark dark:after:border-stone-775"), {
  variants: {
    fullWidth: {
      true: "w-full",
      false: ""
    },
    size: {
      xs: "rounded-[9px]",
      sm: "rounded-xl",
      md: "rounded-[15px]"
    }
  },
  defaultVariants: {
    fullWidth: false,
    size: "sm"
  }
});

// The selected segment's surface: a single pill that translates between options
// instead of each option painting its own background, so a selection change reads
// as movement. On-screen movement, so ease-in-out; 200ms matches SliderToggle.
// Only transform/width/height animate (the indicator is an absolutely positioned
// leaf, so its width/height changes do not relayout siblings).
const indicatorStyles = cva(cn("pointer-events-none absolute left-0 top-0",
// Slight top-to-bottom gradient, lighter on top like the outline Button;
// dark gets the same one-step lift so the pill reads as raised there too.
"bg-linear-to-b from-white to-stone-50", "dark:from-stone-725 dark:to-stone-750",
// A real border for contrast against the track; dark picks a tone one
// step lighter than the pill so the edge reads on the dark track too.
"box-border border border-border-dark dark:border-stone-700", "transition-[transform,width,height] duration-200 ease-in-out", "motion-reduce:transition-none"), {
  // Mirrors Button's per-size radius so the pill hugs the option it sits under.
  variants: {
    size: {
      xs: "rounded-[9px]",
      sm: "rounded-xl",
      md: "rounded-[15px]"
    }
  },
  defaultVariants: {
    size: "sm"
  }
});
const isSameRect = (a, b) => a !== null && a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
/**
 * The container of a segmented, single-select toggle: it owns the selected value
 * (controlled via `value`/`onValueChange` or uncontrolled via `defaultValue`) for
 * its ButtonsSwitch children. Use it to switch between a small set of mutually
 * exclusive views or modes; for triggering actions, use Button or ButtonGroup.
 * @summary Segmented single-select toggle container.
 */
export const ButtonsSwitchList = React.forwardRef(({
  className,
  children,
  size = "sm",
  value,
  defaultValue,
  onValueChange,
  disabled,
  fullWidth,
  ...props
}, ref) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const isControlled = value !== undefined;
  const selected = isControlled ? value : internalValue;
  const handleChange = React.useCallback(next => {
    if (!isControlled) {
      setInternalValue(next);
    }
    onValueChange === null || onValueChange === void 0 || onValueChange(next);
  }, [isControlled, onValueChange]);
  const context = React.useMemo(() => ({
    value: selected,
    onValueChange: handleChange,
    size,
    disabled
  }), [selected, handleChange, size, disabled]);

  // Own the DOM ref for measuring and expose it to the forwarded one without
  // mutating the parameter (see ComposerInput).
  const listRef = React.useRef(null);
  React.useImperativeHandle(ref, () => listRef.current);

  // Position the indicator under the selected option by measuring the DOM:
  // offsets are relative to the list (its offsetParent, via `relative`).
  // Re-measured on selection change, whenever the list or the selected
  // option resizes (font load, fullWidth reflow), and whenever `children`
  // change: a sibling's label changing shifts the selected option without
  // resizing it or (with fullWidth) the list, so the observers stay silent.
  const [indicator, setIndicator] = React.useState(null);
  React.useLayoutEffect(() => {
    const list = listRef.current;
    const active = selected === undefined ? null : list === null || list === void 0 ? void 0 : list.querySelector('[role="tab"][aria-selected="true"]');
    if (!list || !active) {
      setIndicator(null);
      return;
    }
    const measure = () => {
      const next = {
        x: active.offsetLeft,
        y: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight
      };
      setIndicator(prev => isSameRect(prev, next) ? prev : next);
    };
    measure();
    // jsdom (front unit tests) has no ResizeObserver; the initial measure
    // above is enough there.
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    observer.observe(active);
    return () => observer.disconnect();
  }, [selected, children]);
  return /*#__PURE__*/React.createElement("div", {
    ref: listRef,
    role: "tablist",
    "aria-orientation": "horizontal",
    className: cn(listStyles({
      fullWidth,
      size
    }), className),
    ...props
  }, indicator && /*#__PURE__*/React.createElement("div", {
    "aria-hidden": true,
    className: indicatorStyles({
      size
    }),
    style: {
      transform: `translate(${indicator.x}px, ${indicator.y}px)`,
      width: indicator.width,
      height: indicator.height
    }
  }), /*#__PURE__*/React.createElement(ButtonsSwitchContext.Provider, {
    value: context
  }, children));
});
ButtonsSwitchList.displayName = "ButtonsSwitchList";

// Options paint no surface of their own (the list's indicator does). The selected
// one also drops ghost's hover/press tint: hovering it does nothing, and the tint
// would otherwise paint over the track while the indicator is still sliding in.
// Its text color swap shares the indicator's timing so pill and label arrive
// together; inactive options keep Button's snappier hover timing.
const activeOptionStyles = cn("hover:bg-transparent active:bg-transparent", "dark:hover:bg-transparent dark:active:bg-transparent", "duration-200 ease-in-out");
/**
 * One option of a segmented toggle, identified by its `value` and rendered with a
 * `label`. Must be rendered inside a ButtonsSwitchList, which manages selection.
 * @summary Single option of a ButtonsSwitchList.
 */
export const ButtonsSwitch = React.forwardRef(({
  className,
  value,
  label,
  icon,
  disabled,
  onClick,
  ...props
}, ref) => {
  const {
    value: selected,
    onValueChange,
    size,
    disabled: groupDisabled
  } = useButtonsSwitch();
  const isActive = selected === value;
  const isDisabled = disabled || groupDisabled;
  const handleClick = e => {
    if (isDisabled) {
      return;
    }
    onValueChange === null || onValueChange === void 0 || onValueChange(value);
    onClick === null || onClick === void 0 || onClick(e);
  };
  return /*#__PURE__*/React.createElement(Button, {
    ref: ref,
    role: "tab",
    "aria-selected": isActive,
    size: size
    // Inactive: ghost-secondary gives muted text. Active: ghost gives
    // foreground text. Both keep a transparent background; the selected
    // surface is the list's sliding indicator underneath.
    ,
    variant: isActive ? "ghost" : "ghost-secondary",
    label: label,
    icon: icon,
    className: cn(isActive && activeOptionStyles, className),
    disabled: isDisabled,
    onClick: handleClick,
    ...props
  });
});
ButtonsSwitch.displayName = "ButtonsSwitch";
//# sourceMappingURL=ButtonsSwitch.js.map